<template>
    <div>
        <div class="d-flex align-center flex-wrap px-2">
            <v-text-field
                v-model="search"
                dense
                outlined
                single-line
                hide-details
                clearable
                class="mr-2 campo-busca"
                label="Empresa, charge id, uuid.."
                append-icon="mdi-magnify"
                @keypress.enter="aplicar"
                @click:clear="aplicar"
            />

            <v-chip
                v-for="atalho in atalhos"
                :key="atalho.text"
                class="mr-1"
                small
                outlined
                :input-value="atalhoAtivo(atalho)"
                filter
                @click="alternarAtalho(atalho)"
            >
                <v-icon left small>{{ atalho.icone }}</v-icon>
                {{ atalho.text }}
            </v-chip>

            <v-spacer/>

            <PredefinicoesMenu
                :itens="predefinicoes"
                :ativa-id="ativaId"
                :sujo="sujo"
                :nome-em-uso="nomeEmUso"
                @aplicar="aplicarPredefinicao"
                @padrao="aplicarPadrao"
                @salvar="salvarPredefinicao"
                @salvar-como="criarPredefinicao"
                @renomear="renomearPredefinicao"
                @duplicar="duplicarPredefinicao"
                @remover="removerPredefinicao"
            />

            <ColunasMenu v-model="colunas" :catalogo="catalogoColunas"/>

            <FiltrosMenu
                v-model="filtros"
                :catalogo="catalogoFiltros"
                @aplicar="aplicar"
            />

            <PeriodoFilter
                :key="periodoKey"
                v-model="periodo"
                :label="rotuloPeriodo"
                @update="aplicar"
            />
        </div>

        <div v-if="chips.length" class="d-flex flex-wrap align-center px-4 pt-2">
            <v-chip
                v-for="chip in chips"
                :key="chip.chave"
                class="mr-2 mb-1"
                small
                close
                @click:close="removerFiltro(chip.chave)"
            >
                <span class="font-weight-medium mr-1">{{ chip.titulo }}:</span>
                {{ chip.texto }}
            </v-chip>

            <v-btn text x-small class="mb-1" @click="limparFiltros">Limpar tudo</v-btn>
        </div>

        <v-card elevation="2" class="ma-1">
            <v-card-text class="pa-2">
                <v-data-table
                    fixed-header
                    :headers="headers"
                    :items="dados"
                    :mobileBreakpoint="0"
                    :loading="loading"
                    :height="alturaTabela"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    :options.sync="options"
                    :custom-sort="manterOrdemDoServidor"
                    dense
                >
                    <template v-slot:no-data>
                        <div class="py-8 text-center grey--text">
                            <v-icon large color="grey">mdi-filter-remove-outline</v-icon>
                            <div class="mt-2">Nenhuma cobrança para os filtros atuais.</div>
                            <v-btn
                                v-if="chips.length"
                                text
                                small
                                color="primary"
                                class="mt-1"
                                @click="limparFiltros"
                            >Limpar filtros</v-btn>
                        </div>
                    </template>

                    <template v-slot:item.empresa="{ item }">
                        <span
                            class="link"
                            :title="`Abrir financeiro de ${item.empresa_fantasia}`"
                            @click="abrirFinanceiro(item)"
                        >{{ item.empresa_id }} - {{ item.empresa_fantasia }}</span>
                    </template>

                    <template v-slot:item.empresa_ativa="{ item }">
                        <v-icon small :color="item.empresa_ativa ? 'success' : 'error'">
                            mdi-circle-medium
                        </v-icon>
                    </template>

                    <template v-slot:item.plano="{ item }">
                        {{ item.plano_nome }}
                    </template>

                    <template v-slot:item.assinatura_id="{ item }">
                        {{ item.assinatura_external_id || item.assinatura_id }}
                    </template>

                    <template v-slot:item.status_assinatura="{ item }">
                        {{ rotuloDe(statusAssinatura, item.status_assinatura) }}
                    </template>

                    <template v-slot:item.periodicidade="{ item }">
                        {{ rotuloDe(periodicidades, item.periodicidade) }}
                    </template>

                    <template v-slot:item.total="{ item }">
                        R$ {{ formatPrice(item.total) }}
                    </template>

                    <template v-slot:item.total_pago="{ item }">
                        <span :class="corTotalPago(item)">R$ {{ formatPrice(item.total_pago || 0) }}</span>
                    </template>

                    <template v-slot:item.diferenca="{ item }">
                        <span v-if="Number(item.diferenca)" :class="Number(item.diferenca) < 0 ? 'red--text' : 'green--text'">
                            R$ {{ formatPrice(item.diferenca) }}
                        </span>
                        <span v-else>—</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <v-chip x-small dark :color="corStatus(item.status)">
                            {{ rotuloStatus(item.status) }}
                        </v-chip>
                    </template>

                    <template v-slot:item.forma_pagamento="{ item }">
                        {{ rotuloDe(formasPagamento, item.forma_pagamento) }}
                    </template>

                    <template v-slot:item.vencimento="{ item }">
                        {{ formatDate(item.vencimento, 'DD/MM/YYYY') }}
                    </template>

                    <template v-slot:item.paid_at="{ item }">
                        {{ item.paid_at ? formatDate(item.paid_at, 'DD/MM/YYYY') : '—' }}
                    </template>

                    <template v-slot:item.dias_atraso="{ item }">
                        <span v-if="item.dias_atraso === null || item.dias_atraso === undefined">—</span>
                        <span v-else-if="item.dias_atraso > 0" class="red--text">
                            {{ item.dias_atraso }}d
                        </span>
                        <span v-else class="grey--text">em dia</span>
                    </template>

                    <template v-slot:item.nfse_numero="{ item }">
                        <v-tooltip bottom :disabled="!item.nfse_numero">
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on">
                                    <a
                                        v-if="item.nfse_pdf"
                                        :href="item.nfse_pdf"
                                        target="_blank"
                                        :class="item.nfse_cancelado ? 'red--text' : ''"
                                    >{{ item.nfse_numero }}</a>
                                    <span v-else-if="item.nfse_numero">{{ item.nfse_numero }}</span>
                                    <span v-else class="grey--text">—</span>
                                </span>
                            </template>
                            <span>
                                {{ item.nfse_cancelado ? 'Nota cancelada' : `Nota ${item.nfse_status}` }}
                            </span>
                        </v-tooltip>
                    </template>

                    <template v-slot:item.alteracoes_vencimento="{ item }">
                        <span v-if="Number(item.alteracoes_vencimento)">
                            {{ item.alteracoes_vencimento }}x
                        </span>
                        <span v-else class="grey--text">—</span>
                    </template>

                    <template v-slot:item.created_at="{ item }">
                        {{ formatDate(item.created_at, 'DD/MM/YYYY') }}
                    </template>

                    <template v-slot:item.acoes="{ item }">
                        <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn icon x-small v-bind="attrs" v-on="on">
                                    <v-icon small>mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>

                            <v-list dense>
                                <v-list-item @click="abrirFinanceiro(item)">
                                    <v-list-item-icon class="mr-2">
                                        <v-icon small>mdi-open-in-new</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Financeiro da empresa</v-list-item-title>
                                </v-list-item>

                                <v-list-item @click="copiar(item.external_id)">
                                    <v-list-item-icon class="mr-2">
                                        <v-icon small>mdi-content-copy</v-icon>
                                    </v-list-item-icon>
                                    <v-list-item-title>Copiar charge id</v-list-item-title>
                                </v-list-item>

                                <template v-if="emAberto(item)">
                                    <v-divider class="my-1"/>

                                    <v-list-item
                                        v-if="podeMarcarPago"
                                        @click="marcarPago(item)"
                                    >
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small>mdi-check</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Marcar como pago</v-list-item-title>
                                    </v-list-item>

                                    <v-list-item
                                        v-if="podeAlterarVencimento"
                                        @click="alterarVencimento(item)"
                                    >
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small>mdi-calendar-edit</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title>Alterar vencimento</v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>

                <Totalizadores
                    v-if="podeVerTotais"
                    class="mt-2"
                    :itens="itensTotais"
                    :loading="loadingTotais"
                />

                <Paginate v-model="pagination" @input="consultar"/>
            </v-card-text>
        </v-card>

        <AlterarVencimentoDialog
            :visible="dialogVencimento"
            :cobranca="cobrancaSelecionada"
            @close="dialogVencimento = false"
            @salvo="consultar"
        />
    </div>
</template>

<script>
import ColunasMenu from '@/components/ColunasMenu';
import FiltrosMenu from '@/components/FiltrosMenu';
import Paginate from '@/components/Paginate';
import PeriodoFilter from '@/components/PeriodoFilter';
import PredefinicoesMenu from '@/components/PredefinicoesMenu';
import Totalizadores from '@/components/Totalizadores';
import AlterarVencimentoDialog from './AlterarVencimentoDialog';
import consultaMixin from './consultaMixin';
import { catalogoColunasCobrancas } from './colunas';
import { catalogoFiltrosCobrancas } from './filtros';
import {
    ATALHOS_STATUS,
    CAMPOS_DATA_COBRANCA,
    FORMAS_PAGAMENTO,
    PERIODICIDADES,
    STATUS_ASSINATURA,
    corStatus,
    rotuloDe,
    rotuloStatus,
} from './status';

export default {
    name: 'TabCobrancas',

    components: {
        AlterarVencimentoDialog,
        ColunasMenu,
        FiltrosMenu,
        Paginate,
        PeriodoFilter,
        PredefinicoesMenu,
        Totalizadores,
    },

    mixins: [consultaMixin],

    data: () => ({
        endpoint: 'cobrancas/consulta',
        chavePredefinicoes: 'zaper-control-cobrancas-predefinicoes',
        permissaoConsultar: 'zapercontrol_cobrancas_consultar',
        permissaoTotais: 'zapercontrol_cobrancas_totalizadores',
        catalogoColunas: catalogoColunasCobrancas,
        catalogoFiltros: catalogoFiltrosCobrancas,
        colunas: catalogoColunasCobrancas.colunasPadrao(),
        filtros: catalogoFiltrosCobrancas.filtrosPadrao(),

        atalhos: ATALHOS_STATUS,
        formasPagamento: FORMAS_PAGAMENTO,
        periodicidades: PERIODICIDADES,
        statusAssinatura: STATUS_ASSINATURA,

        dialogVencimento: false,
        cobrancaSelecionada: {},
    }),

    computed: {
        headers() {
            return this.colunas
                .map(valor => this.catalogoColunas.coluna(valor))
                .filter(Boolean)
                .map(c => ({
                    text: c.text,
                    value: c.value,
                    sortable: !!c.sortable,
                    align: c.value === 'acoes' ? 'end' : 'start',
                }));
        },

        alturaTabela() {
            const extras = (this.chips.length ? 50 : 0) + (this.podeVerTotais ? 76 : 0);
            return this.$vuetify.breakpoint.height - 300 - extras;
        },

        rotuloPeriodo() {
            return rotuloDe(CAMPOS_DATA_COBRANCA, this.filtros.campo_data) || 'Período';
        },

        podeMarcarPago() {
            return this.checkPermission('zapercontrol_empresas_historico_cobrancas_marcar_pago');
        },

        podeAlterarVencimento() {
            return this.checkPermission('zapercontrol_empresas_historico_cobrancas_alterar_vencimento');
        },

        itensTotais() {
            const t = this.totais || {};
            const real = v => `R$ ${this.formatPrice(v || 0)}`;
            const qtd = v => `${v || 0} cobr.`;

            return [
                { label: 'Cobranças', valor: `${t.qtd || 0}`, detalhe: `${t.qtd_empresas || 0} empresas` },
                { label: 'Emitido', valor: real(t.emitido) },
                { label: 'Recebido', valor: real(t.recebido), detalhe: qtd(t.qtd_pagas), cor: 'green', destaque: true },
                { label: 'Em aberto', valor: real(t.em_aberto), detalhe: qtd(t.qtd_em_aberto), cor: 'blue' },
                { label: 'Vencido', valor: real(t.vencido), detalhe: qtd(t.qtd_vencidas), cor: 'red' },
                { label: 'Cancelado', valor: real(t.cancelado), detalhe: qtd(t.qtd_canceladas), cor: 'grey' },
                { label: 'Ticket médio', valor: real(t.ticket_medio) },
                { label: 'Inadimplência', valor: `${t.inadimplencia_percentual || 0}%` },
                {
                    label: 'Prazo médio pgto',
                    valor: t.prazo_medio_pagamento === null || t.prazo_medio_pagamento === undefined
                        ? '—'
                        : `${t.prazo_medio_pagamento}d`,
                    detalhe: 'após o vencimento',
                },
            ];
        },
    },

    methods: {
        corStatus,
        rotuloStatus,
        rotuloDe,

        atalhoAtivo(atalho) {
            const atual = this.filtros.status || [];

            return atalho.valores.length === atual.length
                && atalho.valores.every(v => atual.includes(v));
        },

        alternarAtalho(atalho) {
            this.filtros = {
                ...this.filtros,
                status: this.atalhoAtivo(atalho) ? [] : [...atalho.valores],
            };
            this.aplicar();
        },

        emAberto(item) {
            return ['new', 'waiting', 'unpaid', 'pendente'].includes(item.status);
        },

        corTotalPago(item) {
            if (!Number(item.total_pago)) {
                return 'grey--text';
            }

            return Number(item.total_pago) < Number(item.total) ? 'orange--text' : 'green--text';
        },

        abrirFinanceiro(item) {
            this.$router.push(`/financeiro/${item.empresa_id}`);
        },

        copiar(valor) {
            navigator.clipboard.writeText(String(valor));
            this.notify('Copiado');
        },

        async marcarPago(item) {
            const ok = await this.$root.$confirm(
                'Marcar como pago',
                `Marcar a cobrança ${item.external_id} de ${item.empresa_fantasia} como paga?`,
            );

            if (!ok) {
                return;
            }

            this.$root.$emit('loading', true);

            this.$http.post('assinaturas/marcar-pago', {
                chargeId: item.external_id,
                empresa_id: item.empresa_id,
            })
                .then(resp => {
                    const data = resp.data.data;

                    if (data && data.type === 'warning') {
                        this.notify(data.msg, 'warning');
                        return;
                    }

                    this.notify('Concluído com sucesso');
                    this.consultar();
                })
                .catch(() => {
                    this.notify('Não foi possivel concluir a operação', 'warning');
                })
                .finally(() => this.$root.$emit('loading', false));
        },

        alterarVencimento(item) {
            this.cobrancaSelecionada = item;
            this.dialogVencimento = true;
        },
    },
};
</script>

<style scoped>
.campo-busca {
    max-width: 260px;
}

.link {
    cursor: pointer;
    text-decoration: underline;
}
</style>
